import React, { useState } from 'react';
import WorkspaceLayout from '../components/Workspace/WorkspaceLayout';

interface DocumentItem {
  id: string;
  title: string;
  type: 'BCP Plan' | 'Tabletop Report' | 'BIA Matrix' | 'Audit Certificate';
  version: string;
  generatedDate: string;
  size: string;
  status: 'Ready' | 'Processing';
}

const DOCUMENTS_LIST: DocumentItem[] = [
  {
    id: 'DOC-01',
    title: 'ISO 22301:2019 Business Continuity Plan - Executive Edition',
    type: 'BCP Plan',
    version: 'v3.2',
    generatedDate: 'Oct 12, 2026',
    size: '2.4 MB',
    status: 'Ready',
  },
  {
    id: 'DOC-02',
    title: 'ISO 22301:2019 Technical Recovery & IT Disaster Playbook',
    type: 'BCP Plan',
    version: 'v3.2',
    generatedDate: 'Oct 12, 2026',
    size: '4.8 MB',
    status: 'Ready',
  },
  {
    id: 'DOC-03',
    title: 'Tabletop Post-Exercise Report: Regional Power Outage (Grade: B+)',
    type: 'Tabletop Report',
    version: 'v1.0',
    generatedDate: 'Oct 02, 2026',
    size: '1.8 MB',
    status: 'Ready',
  },
  {
    id: 'DOC-04',
    title: 'Business Impact Analysis (BIA) Downtime & RTO Matrix',
    type: 'BIA Matrix',
    version: 'v2.1',
    generatedDate: 'Sep 25, 2026',
    size: '890 KB',
    status: 'Ready',
  },
  {
    id: 'DOC-05',
    title: 'Flash AI Resilience Readiness & Insurer Audit Verification Certificate',
    type: 'Audit Certificate',
    version: 'v2026',
    generatedDate: 'Jul 15, 2026',
    size: '512 KB',
    status: 'Ready',
  },
];

export const DocumentsPage: React.FC = () => {
  const [filterType, setFilterType] = useState<string>('all');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = (doc: DocumentItem) => {
    setDownloadingId(doc.id);
    setTimeout(() => {
      setDownloadingId(null);
      // Trigger browser print or download notification
      window.print();
    }, 600);
  };

  const filtered = DOCUMENTS_LIST.filter((d) => {
    if (filterType === 'all') return true;
    return d.type === filterType;
  });

  return (
    <WorkspaceLayout
      title="Documents & Evidence"
      subtitle="Auditable ISO 22301 reports, tabletop exercise evaluations, and resilience certificates"
    >
      <div className="space-y-6">
        {/* Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
              Current Plan Version
            </div>
            <div className="mt-2 text-2xl font-extrabold text-slate-900">v3.2</div>
            <div className="mt-1 text-xs text-slate-500">Last compiled by Planning agent</div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
              Generated Reports
            </div>
            <div className="mt-2 text-2xl font-extrabold text-slate-900">{DOCUMENTS_LIST.length}</div>
            <div className="mt-1 text-xs text-slate-500">Ready for auditor and underwriter download</div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
              Compliance Standard
            </div>
            <div className="mt-2 text-2xl font-extrabold text-[#047857]">ISO 22301:2019</div>
            <div className="mt-1 text-xs text-slate-500">Clauses 4 through 10 aligned</div>
          </div>
        </div>

        {/* Document Table */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 pb-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-base font-bold text-slate-900">Document Repository</h2>

            <div className="flex items-center space-x-2">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-800"
              >
                <option value="all">All Document Types</option>
                <option value="BCP Plan">BCP Plans</option>
                <option value="Tabletop Report">Tabletop Reports</option>
                <option value="BIA Matrix">BIA Matrix</option>
                <option value="Audit Certificate">Certificates</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-100 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  <th className="py-3 px-6">Document Title</th>
                  <th className="py-3 px-4">Type</th>
                  <th className="py-3 px-4">Version</th>
                  <th className="py-3 px-4">Generated Date</th>
                  <th className="py-3 px-4">Size</th>
                  <th className="py-3 px-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((doc) => (
                  <tr key={doc.id} className="hover:bg-slate-50/70 transition-colors">
                    <td className="py-3.5 px-6">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-700 flex items-center justify-center font-bold text-xs flex-shrink-0">
                          PDF
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">{doc.title}</div>
                          <div className="text-[11px] text-slate-400 font-mono">{doc.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3.5 px-4 font-medium text-slate-700">{doc.type}</td>
                    <td className="py-3.5 px-4 font-mono text-slate-600">{doc.version}</td>
                    <td className="py-3.5 px-4 text-slate-500">{doc.generatedDate}</td>
                    <td className="py-3.5 px-4 text-slate-500 font-mono">{doc.size}</td>
                    <td className="py-3.5 px-6 text-right">
                      <button
                        type="button"
                        onClick={() => handleDownload(doc)}
                        disabled={downloadingId === doc.id}
                        className="px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors shadow-sm inline-flex items-center space-x-1.5"
                      >
                        <svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        <span>{downloadingId === doc.id ? 'Downloading...' : 'Download'}</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};

export default DocumentsPage;
